import { useEffect } from 'react';

interface KeyboardShortcutOptions {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  preventDefault?: boolean;
}

/**
 * Hook to listen for keyboard shortcuts
 * @param callback - Function to call when shortcut is triggered
 * @param options - Keyboard shortcut configuration
 * @param enabled - Whether the shortcut is enabled (default: true)
 */
export function useKeyboardShortcut(
  callback: (event: KeyboardEvent) => void,
  options: KeyboardShortcutOptions,
  enabled: boolean = true
) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const {
        key,
        ctrlKey = false,
        metaKey = false,
        shiftKey = false,
        altKey = false,
        preventDefault = true,
      } = options;

      // Check if the pressed key matches
      if (event.key.toLowerCase() !== key.toLowerCase()) return;

      // Check modifiers
      const ctrlOrMeta = ctrlKey || metaKey;
      const hasCorrectModifier =
        (ctrlOrMeta && (event.ctrlKey || event.metaKey)) || (!ctrlOrMeta && !event.ctrlKey && !event.metaKey);

      if (
        hasCorrectModifier &&
        event.shiftKey === shiftKey &&
        event.altKey === altKey
      ) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback, options, enabled]);
}
